import React, { useMemo, useState } from 'react';
import { AiOutlineMail } from 'react-icons/ai';
import { BsArrow90DegDown, BsPeople } from 'react-icons/bs';
import { CgExport } from 'react-icons/cg';
import { RiFolderDownloadLine } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useRouteMatch } from 'react-router-dom';
import {
  Flex,
  Icon,
  MenuItem, Tooltip
} from '@chakra-ui/react';
import qs from 'qs';

import { FeatureFlags } from '../../../../components/Authorizer';
import PopupMenu from '../../../../components/PopupMenu';
import SearchColumnToggle from '../../../../components/search/SearchColumnToggle';
import SearchFilters from '../../../../components/search/SearchFIlters';
import SearchGlobalSearchInput from '../../../../components/search/SearchGlobalSearchInput';
import { analyticsIds } from '../../../../constants/analyticsIds';
import { createErrorToast } from '../../../../redux/modules/app';
import { selectCompanyFeatures } from '../../../../redux/modules/company';
import { insuredSearch } from '../../../../redux/modules/insureds';
import { showModal } from '../../../../redux/modules/modal';
import { reportsResetPresets, selectReportsCheckAllChecked, selectReportsChecked } from '../../../../redux/modules/reports';
import { unique } from '../../../../utils/arrayUtil';
import { closeOpenedQuickFilterInputs } from '../../../../utils/quickFiltersUtil';

import ExportDocumentsModal from './ExportDocumentsModal';
import ExportReportModal from './ExportReportModal';
import MergeInsuredsModal from './MergeInsuredsModal';

const ReportHeader = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { url } = useRouteMatch();
  const checked = useSelector(selectReportsChecked);
  const companyFeatures = useSelector(selectCompanyFeatures);
  const getSearchQueryParams = useSelector(insuredSearch.selectRequestParamsFunction);
  const quickFilters = useSelector(insuredSearch.selectQuickFilterOptions);
  const checkAllChecked = useSelector(selectReportsCheckAllChecked);
  const [disabledMergeLanguage, setDisabledMergeLanguage] = useState('');

  const itemsEnabled = useMemo(() => companyFeatures.find(cf => cf.name === FeatureFlags.Items)?.isEnabled, [companyFeatures]);

  const paramsUpdatedWithCheckedInsureds = () => {
    const params = getSearchQueryParams({ skipScore: true });

    if (!checkAllChecked && Object.keys(checked).length) {
      params.query.bool.filter.push({ bool: { should: Object.keys(checked).map(insuredId => ({ term: { 'insured.id': insuredId } })) } });
    } else if (!checkAllChecked) {
      dispatch(createErrorToast({
        title: 'No insureds selected',
        description: 'Please select at least one insured to execute this command',
        isClosable: true
      }));

      return false;
    }

    return params;
  };

  const handleSendEmail = () => {
    const params = paramsUpdatedWithCheckedInsureds();

    if (!params) return false;

    const draftEmailQueryParams = qs.stringify({ query: JSON.stringify(params) });
    const sendEmailUrl = url.replace('reports', `draft-email?${draftEmailQueryParams}`);
    return history.push(sendEmailUrl);
  };

  const handleExportCsv = () => {
    const params = paramsUpdatedWithCheckedInsureds();

    if (!params) return;

    dispatch(showModal(() => <ExportReportModal searchParams={params} />, {
      title: 'Export Report',
      size: 'md'
    }));
  };

  const handleExportDocuments = () => {
    const params = paramsUpdatedWithCheckedInsureds();

    if (!params) return;

    dispatch(showModal(() => <ExportDocumentsModal searchParams={params} />, {
      title: 'Download Documents',
      size: 'md'
    }));
  };

  const handleMergeInsureds = () => {
    const params = paramsUpdatedWithCheckedInsureds();

    if (!params) return;

    const insureds = Object.values(checked);
    const uniqueInsureds = unique(insureds, insured => insured.insuredId);

    dispatch(showModal(
      () => <MergeInsuredsModal insureds={uniqueInsureds} />,
      {
        title: 'Select the Insured to Keep',
        size: 'md'
      }
    ));
  };

  const mergeInsuredIsDisabled = useMemo(() => {
    const insuredIds = Object.keys(checked);
    const uniqueInsuredIds = unique(insuredIds);

    if (uniqueInsuredIds.length < 2) {
      setDisabledMergeLanguage('Please select more than one insured to merge');

      return true;
    }

    if (uniqueInsuredIds.length > 3) {
      setDisabledMergeLanguage('Please select less than four insureds to merge');

      return true;
    }

    return false;
  }, [checked]);

  const exportIsDisabled = useMemo(() => {
    if (checkAllChecked) {
      return false;
    }

    return !Object.keys(checked).length > 0;
  }, [checkAllChecked, checked]);

  return (
    <Flex
      alignItems="center"
      bgColor="white"
      h="6%"
      maxH="6%"
      paddingX={6}
      paddingY={5}
    >
      <Icon as={BsArrow90DegDown} boxSize={6} mr={1} mt={4} />
      <PopupMenu colorScheme="black" minWidth="160px" title="Batch Actions">
        <Tooltip isDisabled={!exportIsDisabled} label="Please select at least one insured.">
          <MenuItem
            data-analytics-id={analyticsIds.reportsPage.sendEmailButton}
            icon={<AiOutlineMail />}
            isDisabled={exportIsDisabled}
            onClick={handleSendEmail}
          >
            Send Email
          </MenuItem>
        </Tooltip>
        <Tooltip isDisabled={!exportIsDisabled} label="Please select at least one insured">
          <MenuItem
            data-analytics-id={analyticsIds.reportsPage.exportToExcelButton}
            icon={<CgExport />}
            isDisabled={exportIsDisabled}
            onClick={handleExportCsv}
          >
            <Flex align="center" w="100%">
              Export to Excel
            </Flex>
          </MenuItem>
        </Tooltip>

        <Tooltip isDisabled={!exportIsDisabled} label="Please select at least one insured">
          <MenuItem
            data-analytics-id={analyticsIds.reportsPage.downloadDocumentsButton}
            icon={<RiFolderDownloadLine />}
            isDisabled={exportIsDisabled}
            onClick={handleExportDocuments}
          >
            <Flex align="center" w="100%">
              Download Documents (BETA)
            </Flex>
          </MenuItem>
        </Tooltip>

        {!itemsEnabled && (
          <Tooltip isDisabled={!mergeInsuredIsDisabled} label={disabledMergeLanguage}>
            <MenuItem
              data-analytics-id={analyticsIds.reportsPage.exportToExcelButton}
              icon={<BsPeople />}
              isDisabled={mergeInsuredIsDisabled}
              onClick={handleMergeInsureds}
            >
              <Flex align="center" w="100%">
                Merge Insureds
              </Flex>
            </MenuItem>
          </Tooltip>
        )}
      </PopupMenu>
      <SearchGlobalSearchInput
        borderColor="greyScale.900"
        dataAnalyticsId={analyticsIds.reportsPage.searchReportsInput}
        ml="auto"
        mr={4}
        placeholder="Insured Search"
        searchBlueprint={insuredSearch}
      />
      <SearchFilters
        dataAnalyticsId={analyticsIds.reportsPage.reportsFilterPopoverButton}
        searchBlueprint={insuredSearch}
        variant="textBlack"
        onPopoverClose={() => closeOpenedQuickFilterInputs(quickFilters)}
        onReset={() => dispatch(reportsResetPresets())}
      />
      <SearchColumnToggle
        dataAnalyticsId={analyticsIds.reportsPage.openColumnSelectionPopover}
        searchBlueprint={insuredSearch}
        variant="textBlack"
      />
    </Flex>
  );
};

export default ReportHeader;
